import { dark } from '@clerk/themes';

// Clerk's appearance for K2: the single source shared by the global instance (clerk.load) and the
// sign-in modal (openSignIn). Written against the current Clerk UI (Core 3): the base theme goes in
// `theme` — `baseTheme` is silently ignored — and only Core 3 variable names apply (`colorForeground`,
// not `colorText`; `colorInput`, not `colorInputBackground`). An unknown key is dropped without a
// warning, which once left Clerk's dark default text on our dark card (TASK-20261002).
const palette = {
  colorPrimary: '#F7931A', // K2 orange
  colorPrimaryForeground: '#000000', // text on orange primary buttons
  colorBackground: '#1e1f22',
  colorForeground: '#ffffff',
  colorMutedForeground: '#94a3b8',
  colorInput: '#2a2b2e',
  colorInputForeground: '#ffffff',
  borderRadius: '0.5rem'
};

// `@clerk/themes` v2 `dark` inverts these provider glyphs, which suited Core 2's black icons. Core 3
// already draws them light on a dark theme, so the inversion turned them black on our card. Undo it.
const uninvertedProviderIcons = Object.fromEntries(
  Object.keys(dark.elements ?? {})
    .filter((key) => key.startsWith('providerIcon__'))
    .map((key) => [key, { filter: 'none' }])
);

const primaryButton =
  'background: linear-gradient(135deg, #F7931A 0%, #F6C2A0 100%); &:hover { opacity: 0.9; }';

export const clerkAppearance = {
  theme: dark,
  variables: palette,
  elements: {
    ...uninvertedProviderIcons,
    card: 'box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.45);',
    formButtonPrimary: primaryButton
  }
};

// The modal adds only its framing (orange-bordered card, logo, no sign-up footer) to the shared look.
export const signInModalAppearance = {
  ...clerkAppearance,
  elements: {
    ...uninvertedProviderIcons,
    card: 'background-color: #1e1f22 !important; border: 3px solid #F7931A !important; box-shadow: 0 0 30px rgba(247, 147, 26, 0.5), 0 25px 50px -12px rgba(15, 23, 42, 0.45) !important;',
    formButtonPrimary: primaryButton,
    logoImage: 'width: 64px; height: 64px; margin-bottom: 1rem;',
    footerAction: 'display: none;',
    footerActionLink: 'display: none;'
  },
  layout: {
    logoImageUrl: '/k2-logo.png',
    logoPlacement: 'inside',
    showOptionalFields: false
  }
};

// Clerk's browser bundles, pinned (ADR-0041). Left to the loader they resolve by MAJOR tag, so every
// Clerk release reaches the login screen without a deploy of ours. This is the pair Clerk's CDN
// served together on 2026-10-02; bump both on purpose and re-check the sign-in modal visually.
export const clerkScriptVersions = Object.freeze({ clerkJS: '6.36.0', clerkUI: '1.38.0' });

/** WCAG 2.x contrast ratio between two `#rgb` / `#rrggbb` / `#rrggbbaa` colors (alpha ignored). */
export function contrastRatio(foreground, background) {
  const [lighter, darker] = [relativeLuminance(foreground), relativeLuminance(background)]
    .sort((a, b) => b - a);
  return (lighter + 0.05) / (darker + 0.05);
}

function relativeLuminance(hex) {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(hex ?? '');
  if (!match) {
    throw new Error(`Cannot measure contrast of color ${JSON.stringify(hex)}; expected a hex color.`);
  }
  const digits = match[1].length === 3 ? [...match[1]].map((d) => d + d).join('') : match[1];
  const [r, g, b] = [0, 2, 4].map((i) => {
    const channel = parseInt(digits.slice(i, i + 2), 16) / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
